import { Alert, Form, Input, InputNumber, Switch } from '@arco-design/web-react'
import SettingSectionCard from './SettingSectionCard'
import AlertSettingCard from './performance/AlertSettingCard'
import PerformanceThresholdCard from './performance/PerformanceThresholdCard'
import MonitorStatusCard from './performance/MonitorStatusCard'
import type { OptionsAPI } from './useOptions'

const DISK_CACHE_KEYS = [
  'performance_setting.disk_cache_enabled',
  'performance_setting.disk_cache_threshold_mb',
  'performance_setting.disk_cache_max_size_mb',
  'performance_setting.disk_cache_path',
] as const

const MONITOR_KEYS = [
  'performance_setting.monitor_enabled',
  'performance_setting.monitor_cpu_threshold',
  'performance_setting.monitor_memory_threshold',
  'performance_setting.monitor_disk_threshold',
] as const

const STREAM_KEYS = ['StreamCacheQueueLength'] as const

export default function PerformanceSetting({ options }: { options: OptionsAPI }) {
  const { get, getBool, getNumber, patch } = options
  const diskEnabled = getBool('performance_setting.disk_cache_enabled')
  const monitorEnabled = getBool('performance_setting.monitor_enabled')

  return (
    <div className="space-y-0">
      <MonitorStatusCard />
      <AlertSettingCard options={options} />
      <PerformanceThresholdCard options={options} />
      <SettingSectionCard
        title="磁盘缓存（磁盘换内存）"
        subtitle="大请求体临时落盘，降低内存占用；建议在 SSD 环境使用"
        keys={[...DISK_CACHE_KEYS]}
        options={options}
      >
        <Alert
          type="info"
          className="mb-4"
          content="启用后，超过阈值的请求体会写入磁盘缓存目录，而不是常驻内存。"
        />
        <Form layout="vertical">
          <div className="grid md:grid-cols-2 gap-3">
            <Form.Item label="启用磁盘缓存">
              <Switch
                checked={diskEnabled}
                onChange={(v) => patch({ 'performance_setting.disk_cache_enabled': v })}
              />
            </Form.Item>
            <Form.Item label="磁盘缓存阈值 (MB)" extra="请求体超过此大小使用磁盘缓存">
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={1024}
                disabled={!diskEnabled}
                value={getNumber('performance_setting.disk_cache_threshold_mb', 10)}
                onChange={(v) => patch({ 'performance_setting.disk_cache_threshold_mb': v ?? 10 })}
              />
            </Form.Item>
            <Form.Item label="磁盘缓存最大总量 (MB)">
              <InputNumber
                style={{ width: '100%' }}
                min={100}
                max={102400}
                disabled={!diskEnabled}
                value={getNumber('performance_setting.disk_cache_max_size_mb', 1024)}
                onChange={(v) => patch({ 'performance_setting.disk_cache_max_size_mb': v ?? 1024 })}
              />
            </Form.Item>
            <Form.Item label="缓存目录" extra="留空使用系统临时目录">
              <Input
                disabled={!diskEnabled}
                value={get('performance_setting.disk_cache_path')}
                onChange={(v) => patch({ 'performance_setting.disk_cache_path': v })}
                placeholder="例如 /var/cache/new-api"
                allowClear
              />
            </Form.Item>
          </div>
        </Form>
      </SettingSectionCard>

      <SettingSectionCard
        title="系统性能监控"
        subtitle="资源超阈值时拒绝新的 Relay 请求，保护网关稳定性"
        keys={[...MONITOR_KEYS]}
        options={options}
      >
        <Alert
          type="info"
          className="mb-4"
          content="启用后，当 CPU / 内存 / 磁盘使用率超过阈值时，将拒绝 /v1、/v1beta 等新请求。"
        />
        <Form layout="vertical">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Form.Item label="启用性能监控">
              <Switch
                checked={monitorEnabled}
                onChange={(v) => patch({ 'performance_setting.monitor_enabled': v })}
              />
            </Form.Item>
            <Form.Item label="CPU 阈值 (%)">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                disabled={!monitorEnabled}
                value={getNumber('performance_setting.monitor_cpu_threshold', 90)}
                onChange={(v) => patch({ 'performance_setting.monitor_cpu_threshold': v ?? 90 })}
              />
            </Form.Item>
            <Form.Item label="内存阈值 (%)">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                disabled={!monitorEnabled}
                value={getNumber('performance_setting.monitor_memory_threshold', 90)}
                onChange={(v) => patch({ 'performance_setting.monitor_memory_threshold': v ?? 90 })}
              />
            </Form.Item>
            <Form.Item label="磁盘阈值 (%)">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                disabled={!monitorEnabled}
                value={getNumber('performance_setting.monitor_disk_threshold', 95)}
                onChange={(v) => patch({ 'performance_setting.monitor_disk_threshold': v ?? 95 })}
              />
            </Form.Item>
          </div>
        </Form>
      </SettingSectionCard>

      <SettingSectionCard
        title="流式缓存"
        subtitle="StreamCacheQueueLength：流模式缓存队列长度，0 表示不缓存"
        keys={[...STREAM_KEYS]}
        options={options}
      >
        <Form layout="vertical">
          <Form.Item label="流缓存队列长度">
            <InputNumber
              style={{ width: 240 }}
              min={0}
              value={getNumber('StreamCacheQueueLength', 0)}
              onChange={(v) => patch({ StreamCacheQueueLength: v ?? 0 })}
            />
          </Form.Item>
        </Form>
      </SettingSectionCard>
    </div>
  )
}
